import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, StatCard, Badge, Button, ProgressBar, Alert } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import api from '../../services/api';

const PLAN_NAMES = { pack_one: 'Pack One', pack_two: 'Pack Two', pack_advanced: 'Pack Advanced' };
const PLAN_COLORS = { pack_one: '#2ECC71', pack_two: '#3498DB', pack_advanced: '#F39C12' };

export default function ClientDashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState({ sent: 0, opened: 0, clicked: 0, replied: 0, bounced: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [crmStats, setCrmStats] = useState({});
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, campRes, accRes, inboxRes] = await Promise.allSettled([
        api.get('/analytics/overview?days=7'),
        api.get('/campaigns'),
        api.get('/email-accounts'),
        api.get('/inbox/stats'),
      ]);
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data?.stats || {});
      }
      if (campRes.status === 'fulfilled') {
        const d = campRes.value.data;
        setCampaigns(d?.data || d?.campaigns || (Array.isArray(d) ? d : []));
      } else {
        console.error('[Dashboard] campaigns fetch failed:', campRes.reason?.response?.data?.message || campRes.reason?.message);
      }
      if (accRes.status === 'fulfilled') {
        const d = accRes.value.data;
        setAccounts(d?.data || d?.accounts || (Array.isArray(d) ? d : []));
      }
      if (inboxRes.status === 'fulfilled') {
        setUnreadReplies(inboxRes.value.data?.stats?.unread || 0);
      }
    } catch (e) {
      console.error('[Dashboard] fetch error:', e.message);
    } finally { setLoading(false); }
  };

  // Grace period days remaining
  const graceDaysLeft = () => {
    if (!user?.graceStartDate) return 0;
    const days = 10 - Math.floor((Date.now() - new Date(user.graceStartDate)) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  // Subscription days remaining
  const subDaysLeft = () => {
    if (!user?.planExpiresAt) return 0;
    const days = Math.ceil((new Date(user.planExpiresAt) - Date.now()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const isGrace = user?.status === 'grace_period';
  const isPendingPayment = user?.status === 'pending_payment';

  return (
    <div>
      {/* Grace Period Banner */}
      {isGrace && (
        <Alert type="danger" style={{ marginBottom: 24, fontSize: 15 }}>
          🚨 Your subscription has expired. You have <strong>{graceDaysLeft()} days</strong> left in your grace period. After this, your account will be permanently deleted. <Link to="/client/billing" style={{ color: '#E74C3C', fontWeight: 700, textDecoration: 'underline' }}>Renew Now →</Link>
        </Alert>
      )}

      {isPendingPayment && (
        <Alert type="warning" style={{ marginBottom: 24, fontSize: 15 }}>
          ⚠️ Your account is not yet activated. Please complete payment to access all features. <Link to="/client/payment" style={{ color: '#F39C12', fontWeight: 700, textDecoration: 'underline' }}>Choose Plan & Pay →</Link>
        </Alert>
      )}

      {/* Welcome */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800 }}>Welcome back, <span style={{ color: '#2ECC71' }}>{user?.name?.split(' ')[0]}</span> 👋</h1>
        <p style={{ color: '#666', fontSize: 14 }}>{user?.company} · {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      {/* Subscription Status */}
      <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, rgba(46,204,113,0.05), rgba(46,204,113,0.02))', border: '1px solid rgba(46,204,113,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(46,204,113,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📦</div>
            <div>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 600, marginBottom: 2 }}>CURRENT PLAN</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: PLAN_COLORS[user?.planId] || '#2ECC71' }}>{PLAN_NAMES[user?.planId] || 'No Plan'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: isGrace ? '#E74C3C' : '#2ECC71' }}>{isGrace ? graceDaysLeft() : subDaysLeft()}</div>
              <div style={{ fontSize: 11, color: '#666' }}>{isGrace ? 'Grace Days Left' : 'Days Remaining'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{accounts.length}/{user?.accountsLimit === -1 ? '∞' : user?.accountsLimit}</div>
              <div style={{ fontSize: 11, color: '#666' }}>Accounts</div>
            </div>
          </div>
          {!isGrace && <Link to="/client/billing"><Button size="sm" variant="outline">Manage Billing →</Button></Link>}
          {isGrace && <Link to="/client/billing"><Button size="sm">🔄 Renew Now</Button></Link>}
        </div>

        {/* Credit bars */}
        <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <ProgressBar label="Email Sends" value={user?.emailSendsUsed || 0} max={user?.emailSendsLimit || 0} />
          <ProgressBar label="Verifications Used" value={user?.verifyUsed || 0} max={user?.verifyLimit || 0} />
        </div>
      </Card>

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { icon: '📤', label: 'Sent (7d)', value: stats.sent || 0 },
          { icon: '👀', label: 'Opened', value: stats.opened || 0, color: '#3498DB' },
          { icon: '🖱️', label: 'Clicked', value: stats.clicked || 0, color: '#9B59B6' },
          { icon: '💬', label: 'Replied', value: stats.replied || 0, color: '#2ECC71' },
          { icon: '⚠️', label: 'Bounced', value: stats.bounced || 0, color: '#E74C3C' },
        ].map(s => <StatCard key={s.label} icon={s.icon} label={s.label} value={s.value?.toLocaleString()} color={s.color || '#2ECC71'} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Active Campaigns */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700 }}>📧 Recent Campaigns <span style={{ color:'#2ECC71', fontWeight:900 }}>({(campaigns||[]).length})</span></h3>
            <Link to="/client/campaigns"><Button size="sm" variant="ghost">View All</Button></Link>
          </div>
          {campaigns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#555' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <p style={{ fontSize: 13 }}>No campaigns yet</p>
              <Link to="/client/campaigns"><Button size="sm" style={{ marginTop: 12 }}>Create Campaign</Button></Link>
            </div>
          ) : [...(campaigns || [])].sort((a, b) => {
              const order = { active: 0, paused: 1, scheduled: 2, draft: 3, completed: 4 };
              return (order[a.status] ?? 5) - (order[b.status] ?? 5);
            }).slice(0, 5).map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: '#555' }}>{c.statsSent || 0} sent · {c.statsOpened || 0} opened</div>
              </div>
              <Badge color={c.status === 'active' ? 'success' : c.status === 'paused' ? 'warning' : 'gray'}>{c.status}</Badge>
            </div>
          ))}
        </Card>

        {/* Email Accounts */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700 }}>✉️ Email Accounts ({accounts.length}/{user?.accountsLimit === -1 ? '∞' : user?.accountsLimit})</h3>
            <Link to="/client/email-accounts"><Button size="sm" variant="ghost">Manage</Button></Link>
          </div>
          {accounts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#555' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📬</div>
              <p style={{ fontSize: 13 }}>No accounts connected</p>
              <Link to="/client/email-accounts"><Button size="sm" style={{ marginTop: 12 }}>Connect Account</Button></Link>
            </div>
          ) : accounts.slice(0, 5).map(a => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{a.email}</div>
                <div style={{ fontSize: 11, color: '#555' }}>{a.type} · Warmup: {a.warmupScore}/100</div>
              </div>
              <Badge color={a.status === 'active' ? 'success' : a.status === 'warming' ? 'info' : 'danger'}>{a.status}</Badge>
            </div>
          ))}
        </Card>
      </div>

      {/* Quick Actions */}
      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 16 }}>⚡ Quick Actions</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: '+ New Campaign', href: '/client/campaigns', icon: '📧' },
            { label: '+ Import Leads', href: '/client/leads', icon: '👥' },
            { label: '✅ Verify Emails', href: '/client/verifier', icon: '✅' },
            { label: '+ Connect Account', href: '/client/email-accounts', icon: '🔗' },
            { label: `📬 Inbox ${unreadReplies > 0 ? `(${unreadReplies})` : ''}`, href: '/client/inbox', icon: '📬' },
            { label: '📊 Analytics', href: '/client/analytics', icon: '📊' },
          ].map(a => (
            <Link key={a.label} to={a.href} style={{ textDecoration: 'none' }}>
              <Button variant="secondary" size="sm">{a.label}</Button>
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
